import type { Id } from "../../convex/_generated/dataModel";
import { SignOutButton } from "../auth/SignOutButton.tsx";
import type { SessionListItemLite } from "./SessionControl.tsx";

/** Empty/guard states for the admin shell: no active session, or a staff
 *  session URL that does not resolve. Extracted from the retired V1 dashboard
 *  so the shell keeps the explicit no-auto-create flow and its testids. */

export function NoActiveSession({
  sessions,
  optionLabel,
  onCreate,
  onActivate,
}: {
  sessions: SessionListItemLite[] | undefined;
  optionLabel: (s: SessionListItemLite) => string;
  onCreate: () => void;
  onActivate: (sessionId: Id<"sessions">) => void;
}) {
  return (
    <main className="operator no-session" data-testid="no-active-session">
      <header className="topbar">
        <div className="brand">
          <span className="brand-mark" aria-hidden="true" />
          <div className="brand-text">
            <strong title="Conversation Moderator">COMODO</strong>
            <span className="role-badge role-admin" data-testid="role-badge">Admin View</span>
          </div>
        </div>
        <div className="top-meta">
          <span className="pill off" data-testid="top-live">no active session</span>
        </div>
        <div className="top-actions">
          {sessions && sessions.length > 0 && (
            <select
              className="session-select"
              data-testid="session-select"
              defaultValue=""
              onChange={(event) => {
                const value = event.target.value;
                if (value) onActivate(value as Id<"sessions">);
              }}
              title="Switch active session"
            >
              <option value="" disabled>Choose previous session...</option>
              {sessions.map((session) => (
                <option key={session.id} value={session.id}>
                  {optionLabel(session)}
                </option>
              ))}
            </select>
          )}
          <button
            type="button"
            data-testid="new-session"
            className="btn primary sm"
            title="Create a fresh session and make it active"
            onClick={onCreate}
          >
            New session
          </button>
          <a className="btn link ghost sm" data-testid="open-sessions" href="/admin/sessions">Sessions</a>
          <a className="btn link ghost sm" data-testid="open-prompts" href="/admin/llm-settings">LLM Settings</a>
          <SignOutButton redirectTo="/admin" />
        </div>
      </header>
      <section className="no-session-body">
        <h1>No active session</h1>
        <p>Create a new session manually, or choose a previous session from the picker.</p>
        <button type="button" data-testid="no-active-create" className="btn primary big" onClick={onCreate}>
          New session
        </button>
      </section>
    </main>
  );
}

export function StaffSessionUnavailable({ message = "Check the session join code." }: { message?: string }) {
  return (
    <main className="operator no-session">
      <header className="topbar">
        <div className="brand">
          <span className="brand-mark" aria-hidden="true" />
          <div className="brand-text">
            <strong title="Conversation Moderator">COMODO</strong>
            <span className="role-badge role-admin" data-testid="role-badge">Admin View</span>
          </div>
        </div>
        <div className="top-meta">
          <span className="pill off" data-testid="top-live">session unavailable</span>
        </div>
        <div className="top-actions">
          <SignOutButton redirectTo="/admin" />
        </div>
      </header>
      <section className="no-session-body" data-testid="staff-session-unavailable">
        <h1>Session not found</h1>
        <p>{message}</p>
      </section>
    </main>
  );
}
